import { Component, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { WizardStateService, WizardState } from 'src/app/profile/shared/wizard-state.service';
import { ProfileAction } from 'src/app/profile/shared/profile-action.model';

@Component({
  selector: 'app-wizard-progress-banner',
  templateUrl: './wizard-progress-banner.component.html',
  styleUrls: ['./wizard-progress-banner.component.scss'],
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule]
})
export class WizardProgressBannerComponent implements OnInit, OnDestroy {
  wizardState: WizardState | null = null;
  isOnProfilePage = false;

  private subs = new SubSink();

  constructor(
    private readonly wizardStateService: WizardStateService,
    private readonly elRef: ElementRef,
    private readonly router: Router,
    private readonly location: Location
  ) {}

  private resizeObserver: ResizeObserver;

  ngOnInit(): void {
    this.checkIfProfilePage();
    this.subs.sink = this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe(() => this.checkIfProfilePage());

    this.subs.sink = this.wizardStateService.wizardState$.subscribe(state => {
      const identityComplete = this.wizardStateService.isProfileIdentityComplete();
      this.wizardState = (state.active && !identityComplete) ? state : null;
      if (!state.active || identityComplete) {
        this.setBannerHeight(0);
      }
    });

    this.resizeObserver = new ResizeObserver(() => {
      const height = this.wizardState
        ? (this.elRef.nativeElement as HTMLElement).offsetHeight
        : 0;
      this.setBannerHeight(height);
    });
    this.resizeObserver.observe(this.elRef.nativeElement);
  }

  private checkIfProfilePage(): void {
    const path = this.location.path();
    this.isOnProfilePage = path === '/profile' || path === '' || path === '/';
    this.updateBannerHeight();
  }

  private updateBannerHeight(): void {
    if (this.isOnProfilePage || !this.wizardState) {
      this.setBannerHeight(0);
    }
  }

  private setBannerHeight(height: number): void {
    document.documentElement.style.setProperty('--wizard-banner-height', `${height}px`);
  }

  get steps(): ProfileAction[] {
    return this.wizardState?.steps ?? [];
  }

  get currentStepIndex(): number {
    return this.wizardState?.currentStepIndex ?? 0;
  }

  get completedCount(): number {
    return this.steps.filter(s => s.isComplete).length;
  }

  getStepState(index: number): 'complete' | 'active' | 'pending' {
    if (index === this.currentStepIndex) return 'active';
    if (this.steps[index]?.isComplete) return 'complete';
    return 'pending';
  }

  goToStep(index: number): void {
    const step = this.steps[index];
    if (step) {
      this.wizardStateService.setCurrentStep(index);
      step.clickHandler();
    }
  }

  dismiss(): void {
    this.wizardStateService.deactivate();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.resizeObserver?.disconnect();
    this.setBannerHeight(0);
  }
}
